import { SITE_SERVER_URL } from "@/shared/config/player";
import {
    AUTH_TOKEN_KEY,
    PER_USER_CACHE_LOCAL_EXACT,
    PER_USER_CACHE_LOCAL_PREFIXES,
    PER_USER_CACHE_SESSION_PREFIXES,
    SESSION_KEYS,
    USER_LOCALSTORAGE_KEY,
} from "@/shared/config/localstorage";
import type { User } from "@/shared/lib/context/UserContext";
import { getAccountIdentity, resetIdentityCache } from "@/features/identity/api/identityApi";
import { isPlaceholderName } from "@/shared/lib/isPlaceholderName";
import { activityLog } from "@/features/activityLog";
import { isImpersonating } from "@/features/admin/impersonation/lib/impersonationSession";
import { finalizeEmailAuth } from "./storeSessionTokens";

/**
 * Вход по одноразовому коду из приложения или с часов («web handoff»).
 *
 * Часы и приложения открывают страницу сайта в браузере телефона или в своём
 * полноэкранном окне без адресной строки, а человек там часто ни разу не
 * входил. Поэтому приложение (или часы) своим сеансовым ключом получает у
 * site_server одноразовый код (POST /api/auth/web-handoff, живёт 120 с) и
 * открывает адрес с ?code=… Здесь код меняется на полноценный вход сайта
 * (POST /api/auth/web-handoff/redeem) — те же ключи, что кладёт обычный вход, —
 * и сразу убирается из адреса. Сам долгоживущий ключ приложения в адрес не
 * попадает никогда.
 *
 * Код убирает из адреса ещё public/index.html — до Google Tag Manager, чтобы
 * адрес с кодом не ушёл в аналитику (page_location); сам код он оставляет в
 * sessionStorage этой вкладки (см. stashedHandoffCode), так что он переживает
 * и перезагрузку страницы.
 *
 * Чей это ?code= (isHandoffAddress): наш, когда рядом в том же адресе стоит
 * source=app — так приложения открывают ЛЮБУЮ страницу сайта, открытую или
 * кабинетную; и ещё на старом адресе «Зон пульса и длины шага» (HANDOFF_PATH),
 * куда код без source=app шлют уже выпущенные версии приложений и часов.
 * Остальные ?code= (OAuth и прочие) им не считаются. source=app остаётся в
 * адресе, уходит только код.
 *
 * Меняет код на вход WebHandoffGate — один раз на весь сайт и до того, как
 * отрисуется любая страница (и RequireAuth внутри неё).
 *
 * Чужой код не должен тихо пересаживать человека в чужой аккаунт (подложить
 * ссылку с кодом своего аккаунта может кто угодно, а свежий код на каждый
 * переход выпускает и собственный сервер злоумышленника). Поэтому: в браузере
 * уже вошёл другой человек → сначала спрашиваем (settleWebHandoff →
 * "confirm"); не входил никто (или человек согласился сменить аккаунт) →
 * входим, и на ЛЮБОЙ странице сайта, куда бы ни вела ссылка, видно, под кем:
 * полоска «Вы вошли как … Не вы? Выйти ×» (WebHandoffGate, HandoffSignedInBar;
 * см. HANDOFF_NOTICE_KEY). Она держится до «×» или выхода из этого аккаунта и
 * переживает переходы по сайту и перезагрузку вкладки.
 *
 * Рядом с кодом приложение ставит &who=… — чей это аккаунт (см.
 * HANDOFF_WHO_PARAM). Если в браузере уже вошёл ровно этот человек, код не
 * гасится вовсе: ни запроса, ни новой сессии на серверах, ни «Входим в
 * аккаунт…» — страница сразу. who умеет только ПРОПУСТИТЬ вход: поддельный
 * или чужой who оставляет всё как сейчас и никогда никого не впускает и не
 * выпускает.
 */

/**
 * The page the released apps and watches open with a bare handoff code (no
 * source=app): /{lang}/account/training-basis. public/index.html repeats this
 * pattern (its script runs before the bundle); webHandoff.test.ts keeps the
 * two the same.
 */
export const HANDOFF_PATH = /^\/[a-z]{2}\/account\/training-basis\/?$/;

/** An opaque code: URL-safe characters only, sane length. */
const CODE_RE = /^[A-Za-z0-9_-]{8,256}$/;

export const HANDOFF_PARAM = "code";

/**
 * The mark of a link from the app, ?source=app next to the code: with it the
 * code is ours on any page. The mark stays in the address; only the code goes.
 */
export const HANDOFF_SOURCE_PARAM = "source";
export const HANDOFF_SOURCE_APP = "app";

/**
 * Whether a ?code= in this address is a handoff code: the query says
 * source=app (any page), or the page is the old training-basis link. Anything
 * else named code= (OAuth callbacks and the like) is not ours. public/index.html
 * applies the same rule before the analytics start; webHandoff.test.ts keeps
 * the two the same.
 */
export function isHandoffAddress(pathname: string, search: string): boolean {
    if (HANDOFF_PATH.test(pathname)) return true;
    try {
        return new URLSearchParams(search).getAll(HANDOFF_SOURCE_PARAM).includes(HANDOFF_SOURCE_APP);
    } catch {
        return false;
    }
}

/**
 * «Who opened this link»: the app appends &who=<h> right after its code, h
 * naming the account the app is signed in to. When this browser is already
 * signed in as that very account, the code is dropped unspent (no redeem, no
 * new server session, no loader; see handoffKeepsSession). who can only make
 * the site SKIP a sign-in: a forged or stale one keeps the session that is
 * here, and never signs anyone in or out.
 *
 * h = the first 16 lowercase hex characters of SHA-256 over the UTF-8 bytes of
 * "revive-who:" + identity; identity = the account's email, trimmed and
 * lower-cased (handoffWho). The apps compute the same; the tests of every
 * codebase share one golden vector: "Test.User@Example.com " → e060b3d6d692f914.
 *
 * Ours on the same addresses as the code (isHandoffAddress). It leaves the
 * address with the code — public/index.html takes both out before the
 * analytics and stashes who next to the code; useWebHandoff removes what is
 * left — so it reaches neither the analytics nor the activity log nor Back. A
 * who without a code is only taken out.
 */
export const HANDOFF_WHO_PARAM = "who";
export const HANDOFF_WHO_PREFIX = "revive-who:";
const WHO_RE = /^[0-9a-f]{16}$/;

/** A well-formed who, or null. */
export function parseHandoffWho(v: unknown): string | null {
    return typeof v === "string" && WHO_RE.test(v) ? v : null;
}

/** The who of this address (ours only, first one, well-formed), or null. */
export function handoffWhoFrom(pathname: string, search: string): string | null {
    if (!isHandoffAddress(pathname, search)) return null;
    try {
        return parseHandoffWho(new URLSearchParams(search).get(HANDOFF_WHO_PARAM));
    } catch {
        return null;
    }
}

/** A who (well-formed or not) sits in this address of ours: it must leave it. */
export function hasHandoffWho(pathname: string, search: string): boolean {
    if (!isHandoffAddress(pathname, search)) return false;
    try {
        return new URLSearchParams(search).has(HANDOFF_WHO_PARAM);
    } catch {
        return false;
    }
}

/**
 * Where public/index.html leaves the code it took out of the address:
 * sessionStorage (survives a reload of the tab), as {code, who, at};
 * window[HANDOFF_STASH] (and window[HANDOFF_STASH_WHO]) only when
 * sessionStorage is unavailable.
 */
export const HANDOFF_STASH_KEY = "revive_handoff_code";
export const HANDOFF_STASH = "__reviveHandoffCode";
export const HANDOFF_STASH_WHO = "__reviveHandoffWho";
/** A code lives 120 s on the server; an older stash is a leftover, not a code. */
const STASH_TTL_MS = 180_000;

/** A redeem that has not answered by then is given up: the page goes on without it. */
export const REDEEM_TIMEOUT_MS = 20_000;

/** The handoff code in this address, or null when there is none (or it is not one of ours). */
export function handoffCodeFrom(pathname: string, search: string): string | null {
    if (!isHandoffAddress(pathname, search)) return null;
    let code: string | null = null;
    try {
        code = new URLSearchParams(search).get(HANDOFF_PARAM);
    } catch {
        return null;
    }
    return code && CODE_RE.test(code) ? code : null;
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);

type StashWindow = Window & { [HANDOFF_STASH]?: unknown; [HANDOFF_STASH_WHO]?: unknown };

/** A code waiting to be dealt with, and the who that came with it (null when none did). */
export interface PendingHandoff {
    code: string;
    who: string | null;
}

/** The code (and its who) public/index.html took out of the address before the analytics started. */
export function stashedHandoff(): PendingHandoff | null {
    try {
        const raw = sessionStorage.getItem(HANDOFF_STASH_KEY);
        if (raw) {
            const o: unknown = JSON.parse(raw);
            const age = isObj(o) && typeof o.at === "number" ? Date.now() - o.at : NaN;
            const fresh = age >= -60_000 && age < STASH_TTL_MS;
            if (isObj(o) && typeof o.code === "string" && CODE_RE.test(o.code) && fresh) {
                return { code: o.code, who: parseHandoffWho(o.who) };
            }
            // A leftover (the tab was left mid-exchange long ago) or not ours.
            sessionStorage.removeItem(HANDOFF_STASH_KEY);
        }
    } catch {
        /* no sessionStorage, or not ours */
    }
    try {
        const w = window as StashWindow;
        const v = w[HANDOFF_STASH];
        return typeof v === "string" && CODE_RE.test(v) ? { code: v, who: parseHandoffWho(w[HANDOFF_STASH_WHO]) } : null;
    } catch {
        return null;
    }
}

/** The code public/index.html took out of the address before the analytics started. */
export function stashedHandoffCode(): string | null {
    return stashedHandoff()?.code ?? null;
}

export function clearStashedHandoffCode(): void {
    try {
        sessionStorage.removeItem(HANDOFF_STASH_KEY);
    } catch {
        /* ignore */
    }
    try {
        delete (window as StashWindow)[HANDOFF_STASH];
        delete (window as StashWindow)[HANDOFF_STASH_WHO];
    } catch {
        /* ignore */
    }
}

/**
 * The code to deal with on this page, with its who: the one still in the
 * address (index.html did not run, e.g. an old browser, or an in-app link),
 * else the one index.html took out of it — on an address that takes a code at
 * all (source=app stays in it after index.html; the old training-basis link
 * is one by its path). A who goes with its own code only: the address's with
 * the address's code, the stash's with the stash's.
 */
export function pendingHandoff(pathname: string, search: string): PendingHandoff | null {
    if (!isHandoffAddress(pathname, search)) return null;
    const code = handoffCodeFrom(pathname, search);
    if (code) return { code, who: handoffWhoFrom(pathname, search) };
    return stashedHandoff();
}

export function pendingHandoffCode(pathname: string, search: string): string | null {
    return pendingHandoff(pathname, search)?.code ?? null;
}

/** The same query without the code and the who, "" or "?…". */
export function searchWithoutHandoff(search: string): string {
    const sp = new URLSearchParams(search);
    sp.delete(HANDOFF_PARAM);
    sp.delete(HANDOFF_WHO_PARAM);
    const rest = sp.toString();
    return rest ? `?${rest}` : "";
}

export interface WebHandoffSession {
    email: string;
    name: string;
    picture: string;
    siteSessionToken: string;
    /** The bucket worker's session (INT r2_session_token). */
    workerSessionToken: string;
    personalDataSessionToken: string;
    emailIdToken: string;
    user: User | null;
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const first = (o: Obj, keys: string[]): string => {
    for (const k of keys) {
        const s = str(o[k]);
        if (s) return s;
    }
    return "";
};

/** Same test as the Telegram hand-over page: not every login leaves a real address. */
const isRealEmail = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

/**
 * Reads the redeem answer. site_server wraps some answers in {data: …}; the
 * worker session may be named as the login flows name it. Null when there is
 * no email or no site session — that is not a login.
 */
export function readHandoffSession(raw: unknown): WebHandoffSession | null {
    let o: unknown = raw;
    if (isObj(o) && isObj(o.data) && !o.site_session_token) o = o.data;
    if (!isObj(o)) return null;
    const u = isObj(o.user) ? o.user : null;
    const email = (first(o, ["email", "site_user_email"]) || (u ? str(u.email) : "")).toLowerCase();
    const siteSessionToken = first(o, ["site_session_token", "siteSessionToken"]);
    if (!email || !siteSessionToken) return null;
    const name = first(o, ["name", "site_user_name"]) || (u ? str(u.name) : "");
    const picture = first(o, ["picture", "site_user_picture"]) || (u ? str(u.picture) : "");
    const user: User | null =
        u && str(u.id)
            ? {
                  id: str(u.id),
                  email: str(u.email) || email,
                  ...(str(u.name) || name ? { name: str(u.name) || name } : {}),
                  ...(str(u.picture) || picture ? { picture: str(u.picture) || picture } : {}),
              }
            : null;
    return {
        email,
        name,
        picture,
        siteSessionToken,
        workerSessionToken: first(o, [
            "r2_session_token",
            "worker_session_token",
            "session_token",
            "token",
            "app_session_token",
        ]),
        personalDataSessionToken: first(o, ["personal_data_session_token"]),
        emailIdToken: first(o, ["email_id_token"]),
        user,
    };
}

/**
 * Null for a used, stale or foreign code, and for a server that does not
 * answer in REDEEM_TIMEOUT_MS (a cold start, a hung connection): the person
 * must not sit on «Входим в аккаунт…» for ever.
 */
export async function redeemWebHandoff(code: string): Promise<WebHandoffSession | null> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), REDEEM_TIMEOUT_MS);
    try {
        const res = await fetch(`${SITE_SERVER_URL}/api/auth/web-handoff/redeem`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
            cache: "no-store",
            signal: ctrl.signal,
        });
        if (!res.ok) return null;
        return readHandoffSession(await res.json().catch(() => null));
    } finally {
        clearTimeout(timer);
    }
}

function scrubPerUserCaches(): void {
    try {
        const drop: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (
                PER_USER_CACHE_LOCAL_PREFIXES.some((p) => k.startsWith(p)) ||
                PER_USER_CACHE_LOCAL_EXACT.includes(k)
            ) {
                drop.push(k);
            }
        }
        drop.forEach((k) => localStorage.removeItem(k));
    } catch {
        /* best effort */
    }
    try {
        const drop: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && PER_USER_CACHE_SESSION_PREFIXES.some((p) => k.startsWith(p))) drop.push(k);
        }
        drop.forEach((k) => sessionStorage.removeItem(k));
    } catch {
        /* best effort */
    }
}

const read = (k: string): string => {
    try {
        return (localStorage.getItem(k) || "").trim();
    } catch {
        return "";
    }
};

/** The stored "user" object, when it is one. */
function storedUser(): { id: string; email: string; name: string } | null {
    const raw = read(USER_LOCALSTORAGE_KEY);
    if (!raw) return null;
    try {
        const u: unknown = JSON.parse(raw);
        if (isObj(u)) return { id: str(u.id), email: str(u.email).toLowerCase(), name: str(u.name) };
    } catch {
        /* a bare string */
    }
    return { id: "", email: "", name: raw };
}

/** How a person is named on the prompt and the notice: the address, else the name. */
export function handoffLabel(s: Pick<WebHandoffSession, "email" | "name">): string {
    if (isRealEmail(s.email)) return s.email;
    return s.name || s.email;
}

export type HandoffIdentity =
    /** Nobody is signed in in this browser. */
    | { kind: "fresh" }
    /** The same person: only the keys are refreshed. */
    | { kind: "same" }
    /** Someone else (or someone this browser cannot tell apart) is signed in: ask first. */
    | { kind: "other"; current: string };

/**
 * Who is signed in in this browser, compared with the person the code names.
 *
 * Signed in = what RequireAuth goes by (the stored user), or an account_uid
 * (a login that has not written the user yet). The worker and site tokens
 * are not: the menu's «Выйти» leaves them behind (UserProvider.logout), and a
 * signed-out visitor must not be asked whether to leave an account they
 * already left. Nor is site_user_email alone: the support button keeps the
 * address a signed-out visitor typed there. It only tells the same person
 * from another.
 */
export function handoffIdentity(s: WebHandoffSession): HandoffIdentity {
    const user = storedUser();
    const signedIn = !!(user || read("account_uid"));
    if (!signedIn) return { kind: "fresh" };
    // INT accounts are keyed by the address ({email}/… in the bucket; the
    // email login's user id is "email:{address}").
    const fromId = user?.id.startsWith("email:") ? user.id.slice("email:".length).toLowerCase() : "";
    const prevEmail = read("site_user_email").toLowerCase() || user?.email || fromId;
    if (prevEmail && prevEmail === s.email) return { kind: "same" };
    // No address to compare (a Telegram hand-over, say) → not provably the same.
    const current =
        (isRealEmail(prevEmail) ? prevEmail : "") ||
        read("site_user_name") ||
        user?.name ||
        prevEmail ||
        "";
    return { kind: "other", current };
}

/** The identity a who is made of: the email, trimmed and lower-cased. */
export const handoffWhoIdentity = (email: string): string => email.trim().toLowerCase();

const hasSubtleCrypto = (): boolean => {
    try {
        return typeof crypto !== "undefined" && !!crypto.subtle && typeof TextEncoder !== "undefined";
    } catch {
        return false;
    }
};

/**
 * The who of [email] (see HANDOFF_WHO_PARAM); null where this browser cannot
 * hash — crypto.subtle exists on https and localhost only — and then the code
 * is redeemed as usual.
 */
export async function handoffWho(email: string): Promise<string | null> {
    if (!hasSubtleCrypto()) return null;
    try {
        const bytes = new TextEncoder().encode(HANDOFF_WHO_PREFIX + handoffWhoIdentity(email));
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        return Array.from(new Uint8Array(digest).subarray(0, 8), (b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
        return null;
    }
}

/** The who of a link names [expected] (the who of the account signed in here): both well-formed and equal. */
export function sameHandoffWho(expected: string | null, given: string | null): boolean {
    return parseHandoffWho(expected) !== null && parseHandoffWho(given) !== null && expected === given;
}

/** The address an INT user id carries ("email:{address}", the email login's shape), or "". */
const idAddress = (id: string): string => (id.startsWith("email:") ? id.slice("email:".length) : "");

/**
 * The address of the account signed in in this browser ([user], the one
 * RequireAuth goes by), when a who can be compared with it; null otherwise.
 *
 * Signed in means the whole session a redeem puts in place
 * (installWebHandoffSession, or finalizeEmailAuth behind it): the site's key
 * (site_session_token), the bucket worker's (r2_session_token) and the
 * personal-data worker's (personal_data_session_token, or its legacy mirror
 * auth_token). A browser that lacks one is half signed in — the email login
 * keeps the site's key when a worker call fails — and a code redeemed as the
 * same person is what writes the missing keys, so it is redeemed.
 *
 * The address is the user's email, else the one its "email:" id carries.
 * That is deliberate: INT accounts are keyed by that address (the bucket's
 * {email}/…), so such a user is as provably that person as one with an email.
 *
 * No address (a Telegram hand-over, say), not an address, or this browser's
 * own records of the account disagreeing — the stored user (what a reload
 * signs in with; gone once another tab signed out), the "email:" id,
 * site_user_email — → null: not provably that person, so the code is
 * redeemed as usual.
 */
export function handoffSignedInEmail(user: User | string | null | undefined): string | null {
    if (!user || typeof user !== "object") return null;
    if (!read("site_session_token") || !read("r2_session_token")) return null;
    if (!read("personal_data_session_token") && !read(AUTH_TOKEN_KEY)) return null;
    const fromId = idAddress(str(user.id));
    const own = handoffWhoIdentity(str(user.email) || fromId);
    if (!isRealEmail(own)) return null;
    const kept = storedUser();
    if (!kept || handoffWhoIdentity(kept.email || idAddress(kept.id)) !== own) return null;
    for (const other of [fromId, read("site_user_email")]) {
        if (other && handoffWhoIdentity(other) !== own) return null;
    }
    return own;
}

/**
 * Whether the who of a link can be checked at all, without waiting: a
 * well-formed who, a signed-in account with an address, a browser that can
 * hash. Only then is there a moment (the hash, a few ms) with nothing shown.
 */
export function canCheckHandoffWho(who: string | null, user: User | string | null | undefined): boolean {
    return parseHandoffWho(who) !== null && handoffSignedInEmail(user) !== null && hasSubtleCrypto();
}

/** The hash is a few ms; should it ever hang, the code is redeemed rather than the page left blank. */
const WHO_CHECK_TIMEOUT_MS = 1000;

/**
 * True when [who] names the very account signed in here ([user]): the code is
 * then dropped unspent and the session here kept. False in every other case
 * — no who, a malformed or another one, nobody signed in, no address, no
 * crypto.subtle — and the code is redeemed exactly as without a who. Reads
 * only; it never signs anyone in or out.
 */
export async function handoffKeepsSession(
    who: string | null,
    user: User | string | null | undefined,
): Promise<boolean> {
    const email = parseHandoffWho(who) !== null ? handoffSignedInEmail(user) : null;
    if (!email) return false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), WHO_CHECK_TIMEOUT_MS);
    });
    const mine = await Promise.race([handoffWho(email), late]);
    clearTimeout(timer);
    // Looked at again now that the hash is in: the session here may have gone
    // meanwhile (another tab signed out or in as someone else, a 401 handler
    // dropped a key). Only the same account, still signed in, is kept.
    if (handoffSignedInEmail(user) !== email) return false;
    return sameHandoffWho(mine, who);
}

export interface HandoffAuth {
    setUser: (u: User | string) => void;
    setToken?: (token: string) => void;
}

/**
 * Puts the handed-off session in place, exactly where a normal login keeps it.
 *
 * Signed in as someone else in this browser → that session is dropped first,
 * so the previous account's tokens do not linger next to the new ones. The
 * caller asks the person before that (settleWebHandoff → "confirm"). The same
 * person → only the fresh tokens are written. Under «login as» nothing is
 * touched: the admin's backup must survive.
 */
export function installWebHandoffSession(s: WebHandoffSession, auth: HandoffAuth): boolean {
    if (isImpersonating()) return false;
    const who = handoffIdentity(s).kind;
    if (who !== "same") {
        if (who === "other") activityLog.noteLogout("account_switch");
        // The journal names the way in: this is not an email login.
        activityLog.hintLogin("handoff");
        resetIdentityCache();
        for (const k of SESSION_KEYS) {
            try {
                localStorage.removeItem(k);
            } catch {
                /* private mode */
            }
        }
        scrubPerUserCaches();
    }
    const put = (k: string, v: string) => {
        if (!v) return;
        try {
            localStorage.setItem(k, v);
        } catch {
            /* private mode */
        }
    };
    put("site_session_token", s.siteSessionToken);
    put("r2_session_token", s.workerSessionToken);
    if (s.personalDataSessionToken) {
        put("personal_data_session_token", s.personalDataSessionToken);
        // The legacy mirror older readers look at (as finalizeEmailAuth does).
        put(AUTH_TOKEN_KEY, s.personalDataSessionToken);
    }
    // Lets the boot-time ensureR2SessionToken re-issue a dead worker token later.
    put("email_id_token", s.emailIdToken);
    put("site_user_email", s.email);
    if (who !== "same" || s.name) put("site_user_name", s.name);
    put("site_user_picture", s.picture);
    const user: User = s.user ?? {
        id: `email:${s.email}`,
        email: s.email,
        ...(s.name ? { name: s.name } : {}),
        ...(s.picture ? { picture: s.picture } : {}),
    };
    try {
        localStorage.setItem(USER_LOCALSTORAGE_KEY, JSON.stringify(user));
    } catch {
        /* private mode */
    }
    // The provider keeps the key in memory too: it must not stay the previous person's.
    if (who !== "same" || s.personalDataSessionToken) auth.setToken?.(s.personalDataSessionToken);
    auth.setUser(user);
    return true;
}

/** How long the page waits for the workers' half of the login before it shows itself anyway. */
const FINALIZE_WAIT_MS = 6000;
/** How long that half, behind the page, waits for a worker (site_server gives them as long). */
const WORKER_TIMEOUT_MS = 15_000;

/**
 * This browser is still signed in as the person of [s]: what the parts of
 * the login that finish behind the page check before they write anything.
 * «Не вы? Выйти» (or the menu's «Выйти», or a login as someone else) in the
 * meantime must not be undone by a worker that answers after it.
 */
function stillSignedInAs(s: WebHandoffSession): boolean {
    return read("site_user_email").toLowerCase() === s.email && !!read(USER_LOCALSTORAGE_KEY);
}

/**
 * The rest of a normal login when the answer lacks a worker session (the
 * server could not reach a worker): with the email_id_token of the answer,
 * finalizeEmailAuth (the email login's own step) gets the bucket and the
 * personal-data worker sessions and the person's name and photo. The page
 * itself needs only the site session, which is already in place, so a slow
 * worker holds it for [FINALIZE_WAIT_MS] at most; the step still finishes
 * behind it — writing only while this person is still the one signed in
 * (the page may have been left with «Не вы? Выйти» by then).
 */
async function finalizeMissingWorkers(s: WebHandoffSession, auth: HandoffAuth): Promise<void> {
    const stillCurrent = () => stillSignedInAs(s);
    const finalize = Promise.resolve()
        .then(() =>
            finalizeEmailAuth(
                { email_id_token: s.emailIdToken, site_session_token: s.siteSessionToken, email: s.email, name: s.name },
                { setUser: auth.setUser, loginMethod: "handoff", stillCurrent, workerTimeoutMs: WORKER_TIMEOUT_MS },
            ),
        )
        .then(() => {
            // finalizeEmailAuth writes the new mirror to storage only.
            if (stillCurrent()) auth.setToken?.(read(AUTH_TOKEN_KEY));
        })
        .catch(() => undefined);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const wait = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, FINALIZE_WAIT_MS);
    });
    await Promise.race([finalize, wait]);
    clearTimeout(timer);
}

/**
 * The answer carried both worker sessions: nothing to wait for. Only the name
 * and the photo the worker keeps (identity.json, from a Google login on the
 * phone, say) are filled in behind the page — where they are missing, and
 * only while this person is still the one signed in.
 */
function fillIdentityInBackground(s: WebHandoffSession, auth: HandoffAuth): void {
    void getAccountIdentity()
        .then((identity) => {
            if (!identity || !stillSignedInAs(s)) return;
            const patch: Partial<User> = {};
            if (identity.name && isPlaceholderName(localStorage.getItem("site_user_name"))) {
                localStorage.setItem("site_user_name", identity.name);
                patch.name = identity.name;
            }
            // Google's own URL: an <img> cannot send the worker's Bearer header.
            if (identity.googlePicture && !localStorage.getItem("site_user_picture")) {
                localStorage.setItem("site_user_picture", identity.googlePicture);
                patch.picture = identity.googlePicture;
            }
            if (Object.keys(patch).length === 0) return;
            let user: User | null = null;
            try {
                const u: unknown = JSON.parse(localStorage.getItem(USER_LOCALSTORAGE_KEY) || "null");
                if (isObj(u) && str(u.id)) user = u as unknown as User;
            } catch {
                /* not ours */
            }
            if (!user) return;
            const next: User = {
                ...user,
                ...(patch.name && isPlaceholderName(user.name) ? { name: patch.name } : {}),
                ...(patch.picture && !user.picture ? { picture: patch.picture } : {}),
            };
            auth.setUser(next);
        })
        .catch(() => undefined);
}

/** Installs the session and finishes the login: the workers' half only when the answer lacks it. */
export async function completeWebHandoff(s: WebHandoffSession, auth: HandoffAuth): Promise<boolean> {
    if (!installWebHandoffSession(s, auth)) return false;
    if ((!s.workerSessionToken || !s.personalDataSessionToken) && s.emailIdToken) {
        await finalizeMissingWorkers(s, auth);
    } else {
        fillIdentityInBackground(s, auth);
    }
    return true;
}

/**
 * «Вы вошли как …»: a code seated someone in an account in this browser —
 * nobody was signed in here before, or the person agreed to switch. [who]
 * names the account on the bar; [email] is its address (INT accounts are
 * keyed by it): the bar is about that account and goes for good the moment
 * another one, or none, is signed in.
 */
export interface HandoffNotice {
    who: string;
    email: string;
}

/**
 * Where the notice waits: this tab's sessionStorage, not location.state —
 * the bar must be seen on whatever page the link led to and stay over the
 * next ones (route redirects, the login round trip and a reload all drop
 * location.state). In memory only when sessionStorage is unavailable.
 */
export const HANDOFF_NOTICE_KEY = "revive_handoff_signed_in";
let noticeInMemory: HandoffNotice | null = null;

export function handoffNoticeFor(s: Pick<WebHandoffSession, "email" | "name">): HandoffNotice {
    return { who: handoffLabel(s), email: s.email.toLowerCase() };
}

export function saveHandoffNotice(n: HandoffNotice): void {
    noticeInMemory = n;
    try {
        sessionStorage.setItem(HANDOFF_NOTICE_KEY, JSON.stringify(n));
    } catch {
        /* no sessionStorage: the notice lives in memory for this page */
    }
}

export function readHandoffNotice(): HandoffNotice | null {
    try {
        const raw = sessionStorage.getItem(HANDOFF_NOTICE_KEY);
        if (raw) {
            const o: unknown = JSON.parse(raw);
            if (isObj(o) && typeof o.who === "string" && o.who && typeof o.email === "string" && o.email) {
                return { who: o.who, email: o.email.toLowerCase() };
            }
            sessionStorage.removeItem(HANDOFF_NOTICE_KEY);
            return null;
        }
        // sessionStorage works: what it lacks was dismissed, whatever memory says.
        return null;
    } catch {
        return noticeInMemory;
    }
}

export function clearHandoffNotice(): void {
    noticeInMemory = null;
    try {
        sessionStorage.removeItem(HANDOFF_NOTICE_KEY);
    } catch {
        /* ignore */
    }
}

/** The account the notice names is still the one signed in in this browser. */
export function handoffNoticeHolds(n: HandoffNotice): boolean {
    return read("site_user_email").toLowerCase() === n.email && !!read(USER_LOCALSTORAGE_KEY);
}

/**
 * «Не вы? Выйти» on the notice, after UserProvider.logout() (which lets the
 * activity log send its last batch with the key about to go): the keys that
 * logout leaves behind — site_session_token, the workers' sessions — and the
 * per-user caches are the code's owner's, so they go too. An account page
 * then shows the usual login, returning to it; a public page stays, as a guest.
 */
export function dropHandedOffSession(): void {
    clearHandoffNotice();
    for (const k of SESSION_KEYS) {
        try {
            localStorage.removeItem(k);
        } catch {
            /* private mode */
        }
    }
    scrubPerUserCaches();
}

export type HandoffOutcome =
    /** No login from this code (used, stale, foreign, no answer in time, or «login as»). */
    | { kind: "failed" }
    /**
     * Signed in; [notice] is set when nobody was signed in in this browser
     * before (it is already saved: the site says who, see HandoffNotice).
     */
    | { kind: "signedIn"; notice: HandoffNotice | null }
    /** Someone else is signed in here: nothing is changed until the person decides. */
    | { kind: "confirm"; session: WebHandoffSession; current: string; next: string };

/**
 * Redeems the code and signs in — unless another person is signed in in this
 * browser, in which case nothing changes until the person says so.
 */
export async function settleWebHandoff(code: string, auth: HandoffAuth): Promise<HandoffOutcome> {
    // «Login as» keeps the admin's backup session; a code must not replace it.
    if (isImpersonating()) return { kind: "failed" };
    const s = await redeemWebHandoff(code);
    if (!s) return { kind: "failed" };
    const who = handoffIdentity(s);
    if (who.kind === "other") return { kind: "confirm", session: s, current: who.current, next: handoffLabel(s) };
    if (!(await completeWebHandoff(s, auth))) return { kind: "failed" };
    if (who.kind !== "fresh") return { kind: "signedIn", notice: null };
    // Saved here, once per code and whatever the page does meanwhile (the
    // person may have gone Back during the loader): nobody is seated in an
    // account without being told.
    const notice = handoffNoticeFor(s);
    saveHandoffNotice(notice);
    return { kind: "signedIn", notice };
}

/**
 * «Сменить аккаунт» agreed to: the session the code gave is put in place and
 * the site says who is signed in now, as after a fresh sign-in.
 */
export async function acceptWebHandoff(s: WebHandoffSession, auth: HandoffAuth): Promise<HandoffNotice | null> {
    const ok = await completeWebHandoff(s, auth).catch(() => false);
    if (!ok) return null;
    const notice = handoffNoticeFor(s);
    saveHandoffNotice(notice);
    return notice;
}

/**
 * One redemption per code for the whole tab: the code is single use, while
 * StrictMode runs effects twice and the gate may be mounted again (a remount
 * of the app) before the first answer is in.
 */
const redemptions = new Map<string, Promise<HandoffOutcome>>();

export function settleWebHandoffOnce(code: string, auth: HandoffAuth): Promise<HandoffOutcome> {
    let p = redemptions.get(code);
    if (!p) {
        p = settleWebHandoff(code, auth).catch((): HandoffOutcome => ({ kind: "failed" }));
        redemptions.set(code, p);
    }
    return p;
}
